import React from "react";
import "./SupportSection.css";
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Clock,
  CheckCircle2,
  Globe2,
  BarChart3,
  MessageSquare,
  Users,
  Sparkles,
  ArrowRight,
  Bot,
  User as UserIcon,
  ExternalLink,
  Zap,
  Shield,
  TrendingUp
} from 'lucide-react';

const features = [
  {
    icon: Sparkles,
    text: 'Learn from your knowledge base',
    color: 'gradient-purple',
  },
  {
    icon: Globe2,
    text: "Speak your customer's language with multilingual support",
    color: 'gradient-blue',
  },
  {
    icon: BarChart3,
    text: 'Track outcomes automatically',
    color: 'gradient-green',
  },
  {
    icon: MessageSquare,
    text: 'Deploy anywhere: website widget, WhatsApp, and Telegram',
    color: 'gradient-orange',
  },
  {
    icon: Users,
    text: 'Hand off to humans when a request needs a specialist',
    color: 'gradient-pink',
  },
  {
    icon: TrendingUp,
    text: 'See what customers ask most to improve content and reduce tickets',
    color: 'gradient-indigo',
  },
];

const chatMessages = [
  {
    id: 1,
    who: 'user',
    text: "I'd like to cancel my subscription and get a refund.",
  },
  {
    id: 2,
    who: 'bot',
    text: "I can help with that. You're eligible since it's been only 5 days. Our policy allows refunds within 7 days.",
    highlight: ['7 days'],
  },
  {
    id: 3,
    who: 'bot',
    text: "Or, would you like a 30% discount for the next 3 months instead of cancelling?",
    source: 'Subscription & Refunds Policy',
    highlight: ['30% discount'],
  },
  {
    id: 4,
    who: 'user',
    text: "Thanks, I'll continue with the discount.",
  },
  {
    id: 5,
    who: 'bot',
    text: "Done — your discount is active and you've been refunded for 30% of this month's fee.",
    metadata: 'Receipt emailed • Ticket #5931',
    highlight: ['30%'],
  },
];
const SupportSection = () => {
  const [visibleMessages, setVisibleMessages] = useState(0);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    if (isInView && visibleMessages < chatMessages.length) {
      const timer = setTimeout(() => {
        setVisibleMessages(prev => prev + 1);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [visibleMessages, isInView]);

  const handleInView = () => {
    if (!isInView) {
      setIsInView(true);
      setVisibleMessages(1);
    }
  };

  return (
    <section className="support-section-notdemo">
      <div className="supportsection-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="supportsection-header"
        >
          <div className="supportsection-badge">
            <Clock className="badge-icon" />
            <span>Always-On Support</span>
          </div>
          <h2 className="supportsection-title">
            Reduce repetitive customer questions fast,
 
            <br />
            <span className="title-gradient">consistent,</span> {' '}
            
            and <span className="title-gradient">on-brand</span>
          </h2>

          <p className="supportsection-subtitle">
            AI agents that resolve issues instantly while learning from every interaction
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="supportsection-content">
          {/* Left: Chat Demo */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            onViewportEnter={handleInView}
            className="support-chat-wrapper"
          >
            <div className="support-chat-demo-card">
              {/* Chat Header */}
              <div className="support-chat-demo-header">
                <div className="support-chat-agent-info">
                  <div className="support-agent-avatar">
                    <Bot className="support-agent-icon" />
                  </div>
                  <div className="support-agent-details">
                    <div className="support-agent-name">XenaChat</div>
                    <div className="support-agent-status">
                      <span className="status-indicator"></span>
                      <span>AI Agent • Online</span>
                    </div>
                  </div>
                </div>
                <div className="support-chat-user-avatar">
                  <UserIcon className="user-icon" />
                </div>
              </div>

              {/* Chat Messages */}
              <div className="support-chat-demo-messages">
                <AnimatePresence>
                  {chatMessages.slice(0, visibleMessages).map((msg, index) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 20, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                      className={`support-chat-message ${msg.who}`}
                    >
                      <div className="support-message-content">
                        {msg.who === 'bot' && (
                          <div className="support-message-avatar bot">
                            <Bot className="avatar-icon" />
                          </div>
                        )}

                        <div className={`support-message-bubble ${msg.who}`}>
                          <p>{msg.text}</p>
                          
                          {msg.source && (
                            <div className="support-message-source">
                              <ExternalLink className="support-source-icon" />
                              <span>Source: {msg.source}</span>
                            </div>
                          )}

                          {msg.metadata && (
                            <div className="support-message-metadata">{msg.metadata}</div>
                          )}
                        </div>

                        {msg.who === 'user' && (
                          <div className="support-message-avatar user">
                            <UserIcon className="avatar-icon" />
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Typing indicator */}
                {visibleMessages < chatMessages.length && visibleMessages > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="typing-indicator"
                  >
                    <div className="message-avatar bot">
                      <Bot className="avatar-icon" />
                    </div>
                    <div className="typing-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Chat Stats Footer */}
              <div className="chat-demo-footer">
                <div className="stat-pill">
                  <Zap className="stat-icon" />
                  <span>Avg. resolution: <strong>42s</strong></span>
                </div>
                <div className="automation-meter">
                  <div className="meter-bar">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: '70%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.5 }}
                      className="meter-fill"
                    />
                  </div>
                  <span className="meter-label">70% automated</span>
                </div>
              </div>
            </div>

                
          </motion.div>

          {/* Right: Features Grid */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="support-features"
          >
            <div className="features-grid">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="feature-card"
                >
                  <div className={`feature-icon ${feature.color}`}>
                      <feature.icon className="icon" />
                    </div>
                  <p className="feature-text">{feature.text}</p>
                  <div className="feature-check">
                    <CheckCircle2 className="check-icon" />
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              // onClick={onBuildChatbot}
              className="support-cta"
            >
              <span>START FREE, PUBLISH IN MINUTES</span>
              <ArrowRight className="cta-icon" />
            </motion.button>

            {/* Trust Badge */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="trust-badge-support"
            >
              <Shield className="trust-icon" />
              <span>Enterprise-grade security • SOC 2 compliant</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SupportSection;
